import { TextField } from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { FinancialEntryStatus } from '../../components/enum/financialEnums';
import {
  ActionButton as SharedActionButton,
  SearchCard,
  Table as SharedTable,
  TableContainer as SharedTableContainer,
} from '../sharedTable.styled';
import {
  ChartsGrid as WelcomeChartsGrid,
  SectionLabel as WelcomeSectionLabel,
  StatBody as WelcomeStatBody,
  StatCard as WelcomeStatCard,
  StatGrid as WelcomeStatGrid,
  StatIcon as WelcomeStatIcon,
} from '../wellcome/welcome.styled';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.main`
  width: 100%;
  max-width: 1440px;
  min-width: 0;
  margin: 0 auto;
  padding: 12px 20px 48px;
  box-sizing: border-box;
  color: var(--text);

  @media (max-width: 720px) {
    padding: 8px 4px 32px;
  }
`;

export const Title = styled.h1`
  margin: 0 0 6px;
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 3vw, 2.25rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  color: var(--text);
  animation: ${fadeUp} 0.35s ease both;

  &::before {
    content: 'GESTÃO';
    display: block;
    margin-bottom: 5px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: var(--accent);
  }
`;

export const TableContainer = styled(SharedTableContainer)`
  width: 100%;
  box-sizing: border-box;
`;

export const Table = styled(SharedTable)``;

export const ActionButton = styled(SharedActionButton)``;

export const FilterCard = styled(SearchCard)`
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 18px;
`;

export const TextFieldSearch = styled(TextField)`
  min-width: 240px;
  flex: 1 1 280px;
`;

export const StatusCell = styled.td<{ status: FinancialEntryStatus }>`
  font-weight: 700;
  color: ${({ status }) =>
    ({
      [FinancialEntryStatus.PENDING]: '#fbbf24',
      [FinancialEntryStatus.SETTLED]: '#34d399',
      [FinancialEntryStatus.OVERDUE]: '#fb7185',
      [FinancialEntryStatus.CANCELED]: 'var(--text-muted)',
    }[status] || 'var(--text)')};
`;

export const AmountCell = styled.td<{ type: 'INCOME' | 'EXPENSE' }>`
  font-weight: 800;
  color: ${({ type }) => (type === 'INCOME' ? '#34d399' : '#fb7185')};
  white-space: nowrap;
`;

/* Envolve TabsRow + botão de criar, lado a lado -- o botão de criar não deve
   nunca ficar dentro de PageActions/FilterCard, cujo `button {}` legado
   (pensado pro antigo ícone redondo) espreme e sobrescreve o visual do
   CreateButton. */
export const TabsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  width: 92%;
`;

export const TabsRow = styled.nav`
  display: flex;
  width: fit-content;
  max-width: 100%;
  gap: 5px;
  margin: 18px 0 24px;
  padding: 5px;
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface-glass);
  box-shadow: var(--shadow);
  animation: ${fadeUp} 0.4s ease both;

  a {
    padding: 9px 16px;
    border-radius: 10px;
    color: var(--text-muted);
    font-weight: 700;
    font-size: 0.86rem;
    white-space: nowrap;
    text-decoration: none;
    transition: color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
  }

  a:hover {
    color: var(--accent);
    background: var(--surface-2);
  }

  a.active {
    color: #fff;
    background: var(--gradient-brand-soft);
    box-shadow: 0 6px 18px rgba(43, 123, 255, 0.28);
  }
`;

export const ToolbarRow = styled.section`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 26px;
  padding: 16px 18px;
  box-sizing: border-box;
  flex-wrap: wrap;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: var(--shadow);

  & > :last-child {
    margin-left: auto;
  }

  .MuiInputBase-root {
    color: var(--text);
    background: var(--surface-2);
    border-radius: 10px;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: var(--border-strong);
  }

  .MuiSvgIcon-root,
  .MuiInputLabel-root {
    color: var(--text-muted);
  }

  @media (max-width: 640px) {
    align-items: stretch;

    & > *, & > :last-child {
      width: 100%;
      margin-left: 0;
    }
  }
`;

export const SectionLabel = styled(WelcomeSectionLabel)`
  width: 100%;
  margin-top: 0;
`;

export const StatGrid = styled(WelcomeStatGrid)`
  width: 100%;
  grid-template-columns: repeat(5, minmax(0, 1fr));

  @media (max-width: 1240px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled(WelcomeStatCard)`
  min-width: 0;
  padding: 18px;
`;

export const StatIcon = styled(WelcomeStatIcon)``;
export const StatBody = styled(WelcomeStatBody)``;

export const ChartsGrid = styled(WelcomeChartsGrid)`
  width: 100%;
  margin-top: 4px;
`;

/* Estado vazio do ADMIN antes de escolher a empresa. Existe para a tela não
   mentir: sem empresa selecionada, mostrar R$ 0,00 nos cartões pareceria
   "esta empresa não movimentou nada", e não "falta escolher a empresa". */
export const PickCompanyState = styled.div`
  width: 100%;
  margin-top: 8px;
  padding: 40px 24px;
  border: 1px dashed var(--border-strong);
  border-radius: 18px;
  background: var(--surface);
  text-align: center;
  color: var(--text-muted);

  strong {
    display: block;
    margin-bottom: 6px;
    font-family: var(--font-display);
    font-size: 17px;
    font-weight: 700;
    color: var(--text);
  }
`;

export const PageActions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-bottom: 12px;

  button {
    width: 42px;
    height: 42px;
    margin: 0;
    display: grid;
    place-items: center;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface);
    box-shadow: var(--shadow);
  }
`;
